import { useState, useEffect } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AlertTriangle, Users, Calendar as CalendarIcon, Info, CheckCircle2, ChevronRight, ChevronLeft, Pause, Sun, Sunset, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useSuspension } from '@/hooks/useSuspension';
import type {
  SuspensionFormData,
  SuspensionRaison,
  SuspensionDuree,
  SuspensionType,
  SUSPENSION_RAISONS,
  SUSPENSION_DUREES,
} from '@/types/calendar';

// Libellés des raisons et durées (copie locale pour éviter import circulaire)
const RAISONS: Record<SuspensionRaison, string> = {
  intemperies: 'Intempéries',
  attente_materiaux: 'Attente matériaux',
  probleme_client: 'Problème client',
  autre: 'Autre',
};

const DUREES: Record<SuspensionDuree, string> = {
  '1_semaine': '1 semaine',
  '2_semaines': '2 semaines',
  '1_mois': '1 mois',
  'personnalisee': 'Personnalisée',
  'indeterminee': 'Indéterminée',
};

interface ModalSuspensionChantierProps {
  isOpen: boolean;
  onClose: () => void;
  chantierId: string;
  chantierNom: string;
  dateFinEstimee?: string;
  onConfirm: () => void;
}

type Step = 1 | 2 | 3 | 4;

export function ModalSuspensionChantier({
  isOpen,
  onClose,
  chantierId,
  chantierNom,
  dateFinEstimee,
  onConfirm,
}: ModalSuspensionChantierProps) {
  useModalProtection(isOpen);
  const [step, setStep] = useState<Step>(1);
  const [formData, setFormData] = useState<SuspensionFormData>({
    type_suspension: 'totale',
    membres_suspendus: [],
    date_suspension: format(new Date(), 'yyyy-MM-dd'),
    derniere_demi_journee: 'journee_complete',
    raison: 'intemperies',
    raison_detail: '',
    duree_estimee: '1_semaine',
  });

  const {
    loading,
    affectationsActuelles,
    loadAffectationsActuelles,
    suspendreChantier,
  } = useSuspension({
    chantierId,
    onSuccess: () => {
      onConfirm();
      onClose();
    },
  });

  // Charger les affectations au montage
  useEffect(() => {
    if (isOpen) {
      loadAffectationsActuelles();
      setStep(1);
      setFormData({
        type_suspension: 'totale',
        membres_suspendus: [],
        date_suspension: format(new Date(), 'yyyy-MM-dd'),
        derniere_demi_journee: 'journee_complete',
        raison: 'intemperies',
        raison_detail: '',
        duree_estimee: '1_semaine',
      });
    }
  }, [isOpen, loadAffectationsActuelles]);

  // Calculer les affectations à suspendre selon le type
  const affectationsASuspendre = formData.type_suspension === 'totale'
    ? affectationsActuelles
    : affectationsActuelles.filter(a => formData.membres_suspendus.includes(a.membre_equipe_id));


  // Validation de l'étape actuelle
  const isStepValid = (): boolean => {
    switch (step) {
      case 1:
        return true; // Confirmation, toujours valide
      case 2:
        if (formData.type_suspension === 'partielle') {
          return formData.membres_suspendus.length > 0;
        }
        return true;
      case 3:
        // Si raison "autre", le détail doit être rempli
        if (formData.raison === 'autre' && formData.raison_detail.length < 10) {
          return false;
        }
        // Si durée "personnalisée", la date doit être sélectionnée
        if (formData.duree_estimee === 'personnalisee' && !formData.date_reprise_estimee) {
          return false;
        }
        return true;
      case 4:
        return true; // Résumé, toujours valide
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step < 4) {
      setStep((step + 1) as Step);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const handleConfirm = async () => {
    await suspendreChantier(formData);
  };

  const toggleMembre = (membreId: string) => {
    setFormData(prev => ({
      ...prev,
      membres_suspendus: prev.membres_suspendus.includes(membreId)
        ? prev.membres_suspendus.filter(id => id !== membreId)
        : [...prev.membres_suspendus, membreId],
    }));
  };

  // Rendu des étapes
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Icône et message d'avertissement */}
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                <Pause className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Suspendre le chantier</h3>
              <p className="text-muted-foreground">
                Vous êtes sur le point de suspendre le chantier <strong>"{chantierNom}"</strong>.
              </p>
            </div>

            {/* Impacts */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Info className="w-4 h-4 text-primary" />
                Impacts de la suspension
              </h4>
              <ul className="text-sm text-muted-foreground space-y-2 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  Les affectations des membres seront tronquées à la date de suspension
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  Les membres libérés redeviendront disponibles
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  Les informations seront conservées pour faciliter la reprise
                </li>
              </ul>
            </div>

            {/* Info équipe actuelle */}
            <div className="flex items-center justify-between p-3 bg-card rounded-lg border">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm">Équipe actuelle</span>
              </div>
              <span className="font-medium">
                {affectationsActuelles.length} membre{affectationsActuelles.length > 1 ? 's' : ''} affecté{affectationsActuelles.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Type de suspension</h3>

            <div className="space-y-4">
              {/* Suspension totale */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  type_suspension: 'totale',
                  membres_suspendus: [],
                }))}
                className={cn(
                  "w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer text-left",
                  formData.type_suspension === 'totale'
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                )}
              >
                <div className={cn(
                  "w-4 h-4 mt-1 rounded-full border-2 flex-shrink-0",
                  formData.type_suspension === 'totale'
                    ? "border-primary bg-primary"
                    : "border-muted-foreground"
                )} />
                <div className="flex-1">
                  <span className="font-medium">Suspension totale</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Tous les membres sont libérés. Le chantier passe en état "suspendu".
                  </p>
                </div>
              </button>

              {/* Suspension partielle */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  type_suspension: 'partielle',
                }))}
                className={cn(
                  "w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-all cursor-pointer text-left",
                  formData.type_suspension === 'partielle'
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-muted-foreground/50"
                )}
              >
                <div className={cn(
                  "w-4 h-4 mt-1 rounded-full border-2 flex-shrink-0",
                  formData.type_suspension === 'partielle'
                    ? "border-primary bg-primary"
                    : "border-muted-foreground"
                )} />
                <div className="flex-1">
                  <span className="font-medium">Suspension partielle</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Seuls certains membres sont suspendus. Le chantier reste en cours avec les membres restants.
                  </p>
                </div>
              </button>
            </div>

            {/* Sélection des membres (si partielle) */}
            {formData.type_suspension === 'partielle' && (
              <div className="space-y-3">
                <Label>Membres à suspendre</Label>
                <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-3">
                  {affectationsActuelles.map((aff) => (
                    <div
                      key={aff.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleMembre(aff.membre_equipe_id)}
                    >
                      <Checkbox
                        checked={formData.membres_suspendus.includes(aff.membre_equipe_id)}
                        onCheckedChange={() => toggleMembre(aff.membre_equipe_id)}
                      />
                      <span className="flex-1">
                        {aff.membre?.prenom} {aff.membre?.nom}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        jusqu'au {format(new Date(aff.date_fin), 'dd/MM', { locale: fr })}
                      </span>
                    </div>
                  ))}
                </div>
                {formData.type_suspension === 'partielle' && formData.membres_suspendus.length === 0 && (
                  <p className="text-sm text-amber-500">
                    Sélectionnez au moins un membre à suspendre
                  </p>
                )}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Détails de la suspension</h3>

            {/* Dernier jour travaillé */}
            <div className="space-y-3">
              <Label>Dernier jour travaillé</Label>
              <div className="flex items-center gap-3">
                {/* Date picker */}
                <div className="flex-shrink-0">
                  <Input
                    type="date"
                    value={formData.date_suspension}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      date_suspension: e.target.value,
                    }))}
                    className="w-[160px]"
                  />
                </div>

                {/* Sélecteur Journée/Matin/Après-midi - Dropdown */}
                <Select
                  value={formData.derniere_demi_journee}
                  onValueChange={(value: 'journee_complete' | 'matin' | 'apres_midi') => setFormData(prev => ({
                    ...prev,
                    derniere_demi_journee: value,
                  }))}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="journee_complete">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Journée complète</span>
                        <span className="text-xs text-muted-foreground">(8h)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="matin">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4" />
                        <span>Matin uniquement</span>
                        <span className="text-xs text-muted-foreground">(4h)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="apres_midi">
                      <div className="flex items-center gap-2">
                        <Sunset className="w-4 h-4" />
                        <span>Après-midi uniquement</span>
                        <span className="text-xs text-muted-foreground">(4h)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Raison */}
            <div className="space-y-3">
              <Label>Raison de la suspension</Label>
              <Select
                value={formData.raison}
                onValueChange={(value: SuspensionRaison) => setFormData(prev => ({
                  ...prev,
                  raison: value,
                }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionnez une raison" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RAISONS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {formData.raison === 'autre' && (
                <div className="mt-3">
                  <Label>Précisez la raison (min. 10 caractères)</Label>
                  <Textarea
                    value={formData.raison_detail}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      raison_detail: e.target.value,
                    }))}
                    placeholder="Décrivez la raison de la suspension..."
                    className="mt-2"
                    rows={3}
                  />
                  {formData.raison_detail.length < 10 && (
                    <p className="text-xs text-amber-500 mt-1">
                      {10 - formData.raison_detail.length} caractères restants
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Durée estimée */}
            <div className="space-y-3">
              <Label>Durée estimée</Label>
              <Select
                value={formData.duree_estimee}
                onValueChange={(value: SuspensionDuree) => setFormData(prev => ({
                  ...prev,
                  duree_estimee: value,
                  // Reset la date si on change de mode
                  date_reprise_estimee: value === 'personnalisee' ? prev.date_reprise_estimee : undefined,
                }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sélectionnez une durée" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DUREES).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date picker si durée personnalisée */}
            {formData.duree_estimee === 'personnalisee' && (
              <div className="space-y-3">
                <Label>Date de reprise estimée</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.date_reprise_estimee && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date_reprise_estimee
                        ? format(new Date(formData.date_reprise_estimee), 'dd MMMM yyyy', { locale: fr })
                        : 'Sélectionner une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date_reprise_estimee ? new Date(formData.date_reprise_estimee) : undefined}
                      onSelect={(date) => setFormData(prev => ({
                        ...prev,
                        date_reprise_estimee: date ? format(date, 'yyyy-MM-dd') : undefined,
                      }))}
                      disabled={(date) => date < new Date(formData.date_suspension)}
                      locale={fr}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  La date doit être après la date de suspension ({format(new Date(formData.date_suspension), 'dd/MM/yyyy')})
                </p>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Résumé de la suspension</h3>

            {/* Récapitulatif */}
            <div className="bg-muted/50 rounded-lg divide-y divide-border">
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Type</span>
                <span className="font-medium">
                  {formData.type_suspension === 'totale' ? 'Suspension totale' : 'Suspension partielle'}
                </span>
              </div>
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Dernier jour</span>
                <span className="font-medium">
                  {format(new Date(formData.date_suspension), 'dd MMMM yyyy', { locale: fr })}
                  {' '}
                  ({formData.derniere_demi_journee === 'journee_complete'
                    ? 'journée complète - 8h'
                    : formData.derniere_demi_journee === 'matin'
                      ? 'matin - 4h'
                      : 'après-midi - 4h'})
                </span>
              </div>
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Raison</span>
                <span className="font-medium">
                  {RAISONS[formData.raison]}
                  {formData.raison === 'autre' && formData.raison_detail && (
                    <span className="text-sm text-muted-foreground ml-1">
                      ({formData.raison_detail.substring(0, 20)}...)
                    </span>
                  )}
                </span>
              </div>
              <div className="flex justify-between p-3">
                <span className="text-muted-foreground">Durée estimée</span>
                <span className="font-medium">
                  {formData.duree_estimee === 'personnalisee' && formData.date_reprise_estimee
                    ? format(new Date(formData.date_reprise_estimee), 'dd MMMM yyyy', { locale: fr })
                    : DUREES[formData.duree_estimee]}
                </span>
              </div>
            </div>

            {/* Membres libérés */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Membres libérés ({affectationsASuspendre.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {affectationsASuspendre.map((aff) => (
                  <span
                    key={aff.id}
                    className="px-3 py-1 bg-muted rounded-full text-sm"
                  >
                    {aff.membre?.prenom} {aff.membre?.nom}
                  </span>
                ))}
              </div>
            </div>

            {/* Avertissement final */}
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Cette action est réversible</p>
                <p className="text-muted-foreground mt-1">
                  Vous pourrez reprendre le chantier à tout moment avec l'équipe d'origine ou une nouvelle équipe.
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="w-5 h-5 text-amber-500" />
            Suspendre le chantier
          </DialogTitle>
        </DialogHeader>

        {/* Indicateur d'étapes */}
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                  s === step
                    ? "bg-primary text-primary-foreground"
                    : s < step
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {s < step ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 4 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2",
                    s < step ? "bg-green-500" : "bg-muted"
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {/* Contenu de l'étape */}
        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {renderStep()}
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={step === 1 ? onClose : handleBack}
            disabled={loading}
          >
            {step === 1 ? (
              'Annuler'
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour
              </>
            )}
          </Button>

          {step < 4 ? (
            <Button onClick={handleNext} disabled={!isStepValid()}>
              Suivant
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {loading ? 'Suspension...' : 'Confirmer la suspension'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModalSuspensionChantier;
